'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ExpenseItemProps {
  expense: {
    id: string;
    description: string;
    amount: number;
    paidBy: {
      name: string;
      avatar: string;
      alt: string;
    };
    date: string;
    category: string;
    receipt?: string;
    receiptAlt?: string;
    splitWith: string[];
    settled: boolean;
  };
  onEdit: (expenseId: string) => void;
  onDelete: (expenseId: string) => void;
  onToggleSettle: (expenseId: string) => void;
}

const ExpenseItem = ({ expense, onEdit, onDelete, onToggleSettle }: ExpenseItemProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);

  return (
    <div className="glass p-3 lg:p-4 hover:shadow-warm-sm transition-smooth">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full overflow-hidden border-2 border-sage-200/30 flex-shrink-0">
            <AppImage
              src={expense.paidBy.avatar}
              alt={expense.paidBy.alt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-caption font-medium text-foreground mb-1 text-sm lg:text-base break-words">
              {expense.description}
            </h3>
            <p className="text-xs lg:text-sm text-muted-foreground break-words">
              Paid by {expense.paidBy.name} on {expense.date}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2 ml-2">
          <div className="text-right">
            <p className="text-lg lg:text-xl font-heading font-semibold text-foreground">
              ${expense.amount.toFixed(2)}
            </p>
            <span
              className={`inline-block px-2 py-0.5 rounded text-xs font-caption font-medium ${
                expense.settled ? 'bg-success/10 text-success' : 'bg-accent/10 text-accent'
              }`}
            >
              {expense.settled ? 'Settled' : 'Pending'}
            </span>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 rounded hover:bg-muted transition-smooth"
              aria-label="Expense options"
            >
              <Icon
                name="EllipsisVerticalIcon"
                size={20}
                className="text-muted-foreground"
              />
            </button>
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-8 w-40 bg-popover border border-border rounded-lg shadow-warm-md z-50 py-1">
                  <button
                    onClick={() => {
                      onEdit(expense.id);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth flex items-center gap-2"
                  >
                    <Icon name="PencilIcon" size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      onToggleSettle(expense.id);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth flex items-center gap-2"
                  >
                    <Icon name="CheckCircleIcon" size={16} />
                    {expense.settled ? 'Mark Unsettled' : 'Mark Settled'}
                  </button>
                  <button
                    onClick={() => {
                      onDelete(expense.id);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth flex items-center gap-2 text-error"
                  >
                    <Icon name="TrashIcon" size={16} />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-caption bg-sage-50 text-foreground">
          <Icon name="TagIcon" size={12} />
          {expense.category}
        </span>
        <span className="text-xs font-caption text-muted-foreground break-words">
          Split with: {expense.splitWith.join(', ')}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {expense.receipt && (
          <button
            onClick={() => setShowReceipt(true)}
            className="flex items-center gap-1 text-xs font-caption text-primary hover:text-primary/80 transition-smooth"
          >
            <Icon name="DocumentTextIcon" size={14} />
            View Receipt
          </button>
        )}
      </div>

      {showReceipt && expense.receipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="glass rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-sage-200/30">
              <h3 className="font-heading text-lg font-semibold text-foreground">
                Receipt
              </h3>
              <button
                onClick={() => setShowReceipt(false)}
                className="p-2 rounded-lg hover:bg-muted transition-smooth"
                aria-label="Close receipt"
              >
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              <AppImage
                src={expense.receipt}
                alt={expense.receiptAlt || 'Receipt image for expense'}
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseItem;